// Libs
import classNames from 'classnames/bind';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Grid2, IconButton, Stack, Typography } from '@mui/material';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
// Components, Layouts, Pages
import { BaseInput, BaseSelect } from '~/components/common';
// Others
import { useAppDispatch } from '~/utils/redux/hooks';
import { IPayloadCallApiFn, IResultCallApiFn } from '~/utils/interface/common';
import { formatInputCurrency, generateUniqueId } from '~/utils/helper/common';
import { IInventoryProduct, IParamInventoryProduct } from '~/utils/interface/inventory';
import { IFormPurchaseOrder, IOptionProductItem, IPurchaseOrderItem } from '~/utils/interface/purchaseOrder';
import {
  DEFAULT_LIMIT_PAGE,
  DEFAULT_SELECT_DYNAMIC_RESULT_FETCH_DATA,
  DOLLAR,
  EMPTY_STRING,
  REG_EXP,
} from '~/utils/constants/common';
import { getListInventoryProduct } from '~/thunks/inventory/inventoryThunk';
// Styles, images, icons
import styles from './PurchaseOrderProductInfoForm.module.scss';
import { icons } from '~/assets';

type Props = { multiple?: boolean };

const cx = classNames.bind(styles);

const PurchaseOrderProductInfoForm = (props: Props) => {
  //#region Destructuring Props
  const { multiple = false } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/purchaseOrder', 'common/base']);
  const dispatch = useAppDispatch();

  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<IFormPurchaseOrder>();

  const { purchaseOrderItems, deleteItemIds } = watch();

  const { append, remove } = useFieldArray({
    control,
    name: 'purchaseOrderItems',
  });
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  //#endregion Implement Hook

  //#region Handle Function
  const handleAddProductItem = () => {
    const newProductItem: IPurchaseOrderItem = {
      id: generateUniqueId(),
      inventoryId: EMPTY_STRING,
      variantId: EMPTY_STRING,
      unitPrice: EMPTY_STRING,
      quantity: EMPTY_STRING,
      total: EMPTY_STRING,
      idRemove: EMPTY_STRING,
    };

    append(newProductItem);
  };

  const fetchOptionsListProductItem = useCallback(
    async (payload: IPayloadCallApiFn): Promise<IResultCallApiFn<IOptionProductItem>> => {
      const { page, searchKey } = payload;
      const payloadParams: IParamInventoryProduct = {
        page: page,
        limit: DEFAULT_LIMIT_PAGE,
        isHasVariant: true,
        ...(searchKey && { textSearch: searchKey }),
      };

      try {
        const resp = await dispatch(getListInventoryProduct(payloadParams)).unwrap();

        const { responses: listInventory, pagination } = resp?.data;

        const formatOptions: IOptionProductItem[] = handleConvertOptionAddOn(listInventory);

        const hasMore: boolean = pagination?.page < pagination?.totalPages || false;

        return { optionsResp: formatOptions, hasMoreResp: hasMore };
      } catch (error) {
        return DEFAULT_SELECT_DYNAMIC_RESULT_FETCH_DATA;
      }
    },
    []
  );

  const handleConvertOptionAddOn = (data: IInventoryProduct[]): IOptionProductItem[] => {
    const options: IOptionProductItem[] = data.map((item) => ({
      value: `${item.id}_${item.variantId}`,
      productId: item.id,
      label: `${item.name} ${item?.variantName ? ` - ${item?.variantName}` : EMPTY_STRING}`,
      price: item.rentalPrice,
      variantId: item.variantId,
    }));

    return options;
  };
  //#endregion Handle Function

  return (
    <Stack id='purchaseOrderProductInfoFormComponent' className={cx('container')} gap={5}>
      <Typography className={cx('title')}>{t('purchase_order_form_title_product_information')}</Typography>

      {purchaseOrderItems && purchaseOrderItems.length > 0 && (
        <Stack className={cx('purchaseOrderItemsContainer')} spacing={4}>
          {purchaseOrderItems?.map((item, index) => {
            const filedName = `purchaseOrderItems.${index}` as const;

            return (
              <Grid2 key={index} columnSpacing={'20px'} rowSpacing={'20px'} container>
                <Grid2 size={multiple ? 3 : 4}>
                  <Controller
                    key={item.id}
                    control={control}
                    name={`${filedName}.idRemove`}
                    render={({ field: { onChange, value } }) => (
                      <BaseSelect<IOptionProductItem>
                        isSearch
                        gap={8}
                        height={37}
                        value={value}
                        itemNameDefault={item?.name}
                        label={t('purchase_order_form_title_product_name')}
                        callApiFn={fetchOptionsListProductItem}
                        type='loadMore'
                        onChange={(selected) => {
                          onChange(selected?.value);

                          setValue(
                            `purchaseOrderItems.${index}`,
                            {
                              ...item,
                              inventoryId: selected?.productId || EMPTY_STRING,
                              variantId: selected?.variantId || EMPTY_STRING,
                              unitPrice: formatInputCurrency(`${selected?.price}`),
                              total: formatInputCurrency(
                                `${Number(purchaseOrderItems[index].quantity) * Number(selected?.price)}`
                              ),
                            },
                            { shouldDirty: true, shouldValidate: true }
                          );
                        }}
                        errorMessage={errors.purchaseOrderItems?.[index]?.inventoryId?.message}
                      />
                    )}
                  />
                </Grid2>

                <Grid2 size={2}>
                  <Controller
                    key={item.id}
                    control={control}
                    name={`${filedName}.quantity`}
                    render={({ field: { onChange, value } }) => (
                      <BaseInput
                        height={37}
                        label={t('purchase_order_form_title_quantity')}
                        placeholder={t('common_text_placeholder_number', { ns: 'common/base' })}
                        value={value}
                        onChange={(e) => {
                          const inputValue = e.target.value.replace(REG_EXP.NUMBER, EMPTY_STRING);
                          const numericValue = inputValue ? Number(inputValue) : EMPTY_STRING;
                          onChange(numericValue);
                          setValue(
                            `purchaseOrderItems.${index}`,
                            {
                              ...item,
                              total: formatInputCurrency(
                                `${Number(purchaseOrderItems[index].unitPrice) * Number(numericValue)}`
                              ),
                            },
                            { shouldDirty: true, shouldValidate: true }
                          );
                        }}
                      />
                    )}
                  />
                </Grid2>

                <Grid2 size={3}>
                  <Controller
                    key={item.id}
                    control={control}
                    name={`${filedName}.unitPrice`}
                    render={({ field: { onChange, value } }) => (
                      <BaseInput
                        height={37}
                        label={t('purchase_order_form_title_unit_price')}
                        placeholder={t('common_text_placeholder_currency', { ns: 'common/base' })}
                        value={value}
                        startAdornment={<Typography className={cx('startAdornment')}>{DOLLAR}</Typography>}
                        onChange={(event) => {
                          onChange(formatInputCurrency(event.target.value));

                          setValue(
                            `purchaseOrderItems.${index}`,
                            {
                              ...item,
                              total: formatInputCurrency(
                                `${
                                  Number(purchaseOrderItems[index].quantity) *
                                  Number(formatInputCurrency(event.target.value))
                                }`
                              ),
                            },
                            { shouldDirty: true, shouldValidate: true }
                          );
                        }}
                      />
                    )}
                  />
                </Grid2>

                <Grid2 size={3}>
                  <Controller
                    key={item.id}
                    control={control}
                    name={`${filedName}.total`}
                    render={({ field: { onChange, value } }) => (
                      <BaseInput
                        height={37}
                        label={t('purchase_order_form_title_subtotal')}
                        placeholder={t('common_text_placeholder_currency', { ns: 'common/base' })}
                        value={value}
                        startAdornment={<Typography className={cx('startAdornment')}>{DOLLAR}</Typography>}
                        onChange={(event) => {
                          onChange(formatInputCurrency(event.target.value));
                        }}
                      />
                    )}
                  />
                </Grid2>

                {multiple && (
                  <Grid2 size={1}>
                    <Box>
                      <IconButton
                        key={item.id}
                        className={cx('buttonIcon')}
                        onClick={() => {
                          remove(index);

                          if (item?.id && item?.id?.length < 15)
                            setValue('deleteItemIds', [...(deleteItemIds || []), String(item.id)]);
                        }}
                      >
                        <img
                          className={cx('iconDelete')}
                          src={icons.commonIconTrash}
                          alt={t('common_alt_img', { ns: 'common/base' })}
                        />
                      </IconButton>
                    </Box>
                  </Grid2>
                )}
              </Grid2>
            );
          })}
        </Stack>
      )}
      {multiple && (
        <Typography component={'span'} className={cx('buttonAdd')} onClick={handleAddProductItem}>
          {t('common_button_add_item', { ns: 'common/base' })}
        </Typography>
      )}
    </Stack>
  );
};

export default PurchaseOrderProductInfoForm;
